import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Menu,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  MoreVert as MoreVertIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { useDroppable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { kanbanService } from '../../services/kanbanService';
import KanbanCard from './KanbanCard';

interface KanbanListProps {
  list: {
    id: string;
    name: string;
    position: number;
    board_id: string;
  };
  cards: Array<{
    id: string;
    title: string;
    description?: string;
    position: number;
    due_date?: string;
    is_completed?: boolean;
    cover_image_url?: string;
    members?: string[];
    labels?: string[];
  }>;
  totalCards?: number;
  hasMoreCards?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
  visibleCount?: number;
  prevList?: any | null;
  nextList?: any | null;
  onMoveCard?: (cardId: string, targetListId: string) => void;
  onMoveCardInList?: (cardId: string, targetPosition: number) => void;
}

const KanbanList: React.FC<KanbanListProps> = ({ 
  list, 
  cards,
  totalCards = cards.length,
  hasMoreCards = false,
  isLoadingMore = false,
  onLoadMore,
  visibleCount = cards.length,
  prevList = null,
  nextList = null,
  onMoveCard,
  onMoveCardInList,
}) => {
  const { boardId } = useParams<{ boardId: string }>();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDark = theme.palette.mode === 'dark';
  const listWidth = isMobile ? 260 : 272;
  const [showAddCard, setShowAddCard] = useState(false);
  const [newCardTitle, setNewCardTitle] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: list.id,
    disabled: true, // Desabilitar drag de lista por enquanto
  });

  // Tornar a lista um droppable para permitir soltar cartões nela
  const { setNodeRef: setDroppableRef, isOver } = useDroppable({
    id: list.id,
  });

  // Combinar refs
  const combinedRef = (node: HTMLElement | null) => {
    setNodeRef(node);
    setDroppableRef(node);
  };

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const createCardMutation = useMutation({
    mutationFn: (title: string) =>
      kanbanService.createCard({
        list_id: list.id,
        board_id: list.board_id,
        title,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kanban-board', boardId] });
      setShowAddCard(false);
      setNewCardTitle('');
    },
  });

  const deleteListMutation = useMutation({
    mutationFn: () => kanbanService.deleteList(list.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kanban-board', boardId] });
    },
  });

  const handleAddCard = () => {
    if (!newCardTitle.trim()) return;
    createCardMutation.mutate(newCardTitle);
  };

  const handleDeleteList = () => {
    if (window.confirm('Tem certeza que deseja excluir esta lista?')) {
      deleteListMutation.mutate();
    }
    setAnchorEl(null);
  };

  // Função para verificar se o card tem label vermelha
  const hasRedLabel = (card: any): boolean => {
    if (!card.labels || !Array.isArray(card.labels)) return false;
    return card.labels.some((label: any) => {
      // Verificar se é objeto com color ou se precisa buscar
      if (label && typeof label === 'object' && label.color) {
        // Cores vermelhas possíveis: #EB5A46 (padrão), #ff0000, #f00, etc.
        const redColors = ['#EB5A46', '#eb5a46', '#FF0000', '#ff0000', '#F00', '#f00', '#DC143C', '#dc143c'];
        return redColors.includes(label.color.toUpperCase()) || 
               label.color.toLowerCase().includes('red') ||
               // Verificar se é vermelho por RGB aproximado
               (label.color.startsWith('#') && 
                parseInt(label.color.slice(1, 3), 16) > 200 && 
                parseInt(label.color.slice(3, 5), 16) < 100 && 
                parseInt(label.color.slice(5, 7), 16) < 100);
      }
      return false;
    });
  };

  // Ordenar cards: primeiro os com label vermelha, depois os outros
  // Dentro de cada grupo, manter ordem por position
  const sortedCards = [...cards].sort((a, b) => {
    const aHasRed = hasRedLabel(a);
    const bHasRed = hasRedLabel(b);
    
    // Se ambos têm ou não têm label vermelha, ordenar por position
    if (aHasRed === bHasRed) {
      return a.position - b.position;
    }
    
    // Cards com label vermelha vêm primeiro
    return aHasRed ? -1 : 1;
  });

  return (
    <Box
      ref={setNodeRef}
      style={style}
      {...attributes}
      sx={{
        minWidth: listWidth,
        maxWidth: listWidth,
        display: 'flex',
        flexDirection: 'column',
        height: isMobile ? 'calc(100dvh - 180px)' : 'calc(100vh - 200px)',
        maxHeight: isMobile ? 'calc(100dvh - 180px)' : 'calc(100vh - 200px)',
        flexShrink: 0,
      }}
    >
      <Box
        ref={combinedRef}
        sx={{
          p: isMobile ? 1.5 : 2,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          maxHeight: '100%',
          overflow: 'hidden',
          borderRadius: 2,
          bgcolor: theme.palette.mode === 'dark'
            ? alpha(theme.palette.primary.main, 0.08)
            : alpha(theme.palette.primary.main, 0.05),
          border: isOver
            ? `2px solid ${theme.palette.primary.main}`
            : `1px solid ${theme.palette.divider}`,
          boxShadow: isOver ? theme.shadows[4] : theme.shadows[1],
          transition: 'all 0.2s ease',
          transform: isOver ? 'scale(1.02)' : 'scale(1)',
        }}
      >
        {/* Header */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6" sx={{ fontWeight: 600, flexGrow: 1, color: 'text.primary', fontSize: '1rem' }}>
            {list.name}
          </Typography>
          <IconButton
            size="small"
            onClick={(e) => setAnchorEl(e.currentTarget)}
            {...listeners}
            sx={{ color: 'primary.main', '&:hover': { bgcolor: 'action.hover' } }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          PaperProps={{
            sx: {
              bgcolor: 'background.paper',
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              mt: 1,
              boxShadow: theme.shadows[8],
            },
          }}
        >
          <MenuItem onClick={handleDeleteList} sx={{ color: 'text.primary', '&:hover': { bgcolor: 'action.hover' } }}>
            <DeleteIcon fontSize="small" sx={{ mr: 1, color: 'error.main' }} />
            Excluir Lista
          </MenuItem>
        </Menu>

        {/* Add Card - Movido para o topo */}
        {showAddCard ? (
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Título do cartão"
              value={newCardTitle}
              onChange={(e) => setNewCardTitle(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') handleAddCard();
                if (e.key === 'Escape') {
                  setShowAddCard(false);
                  setNewCardTitle('');
                }
              }}
              autoFocus
              sx={{ mb: 1 }}
            />
            <Box display="flex" gap={1}>
              <Button
                size="small"
                variant="contained"
                onClick={handleAddCard}
                disabled={!newCardTitle.trim() || createCardMutation.isPending}
                sx={{ color: '#fff', '&:hover': { color: '#fff' } }}
              >
                Adicionar
              </Button>
              <Button
                size="small"
                onClick={() => {
                  setShowAddCard(false);
                  setNewCardTitle('');
                }}
                variant="outlined"
                color="inherit"
                sx={{ color: 'text.secondary', borderColor: 'divider', '&:hover': { borderColor: 'divider', bgcolor: 'action.hover' } }}
              >
                Cancelar
              </Button>
            </Box>
          </Box>
        ) : (
          <Button
            fullWidth
            startIcon={<AddIcon />}
            onClick={() => setShowAddCard(true)}
            sx={{
              justifyContent: 'flex-start',
              mb: 2,
              color: 'text.secondary',
              borderColor: 'divider',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover', color: 'text.primary' },
            }}
            variant="outlined"
          >
            Adicionar cartão
          </Button>
        )}

        {/* Cards */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            mb: 2,
            '&::-webkit-scrollbar': { width: 8 },
            '&::-webkit-scrollbar-track': { bgcolor: 'action.hover', borderRadius: 1 },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: 'divider',
              borderRadius: 1,
              '&:hover': { bgcolor: 'text.secondary' },
            },
            scrollbarWidth: 'thin',
            scrollbarColor: `${theme.palette.divider} ${theme.palette.action.hover}`,
          }}
        >
          <SortableContext items={sortedCards.map((c) => c.id)} strategy={verticalListSortingStrategy}>
            {sortedCards.map((card, index) => {
              // Calcular se pode mover para cima/baixo baseado na posição absoluta
              const currentCardIndex = sortedCards.findIndex(c => c.id === card.id);
              const canMoveUpInVisible = currentCardIndex > 0;
              const canMoveDownInVisible = currentCardIndex < sortedCards.length - 1;
              
              // Para calcular a posição absoluta, precisamos encontrar o card acima/abaixo
              // e usar sua posição
              let targetPositionUp = -1;
              let targetPositionDown = -1;
              
              if (canMoveUpInVisible && currentCardIndex > 0) {
                const cardAbove = sortedCards[currentCardIndex - 1];
                targetPositionUp = cardAbove.position;
              }
              
              if (canMoveDownInVisible && currentCardIndex < sortedCards.length - 1) {
                const cardBelow = sortedCards[currentCardIndex + 1];
                targetPositionDown = cardBelow.position;
              }
              
              return (
                <KanbanCard 
                  key={card.id} 
                  card={card}
                  prevList={prevList}
                  nextList={nextList}
                  onMoveCard={onMoveCard}
                  canMoveUp={canMoveUpInVisible}
                  canMoveDown={canMoveDownInVisible}
                  onMoveCardUp={(cardId) => {
                    if (targetPositionUp >= 0 && onMoveCardInList) {
                      onMoveCardInList(cardId, targetPositionUp);
                    }
                  }}
                  onMoveCardDown={(cardId) => {
                    if (targetPositionDown >= 0 && onMoveCardInList) {
                      onMoveCardInList(cardId, targetPositionDown);
                    }
                  }}
                />
              );
            })}
          </SortableContext>
          
          {/* Botão para carregar mais cards se houver mais */}
          {hasMoreCards && onLoadMore && (
            <Box sx={{ mt: 1, textAlign: 'center' }}>
              <Button
                variant="outlined"
                size="small"
                onClick={onLoadMore}
                disabled={isLoadingMore}
                fullWidth
                sx={{
                  fontSize: '0.75rem',
                  py: 0.5,
                  color: 'text.secondary',
                  borderColor: 'divider',
                  bgcolor: 'action.hover',
                  '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover', color: 'primary.main' },
                  '&:disabled': { opacity: 0.6 },
                }}
              >
                {isLoadingMore ? (
                  <CircularProgress size={16} sx={{ color: 'primary.main' }} />
                ) : (
                  `Ver mais (${totalCards - visibleCount} restantes)`
                )}
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default KanbanList;

